import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  CogIcon,
  UserIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function AdminSettings() {
  const { currentUser, hasPermission } = useAuth();
  const [settings, setSettings] = useState({
    salesManagerEmail: '',
    allowManagerPurchases: false,
    allowManagerSuppliers: false,
    allowManagerReports: false,
    allowManagerInventory: false,
    allowManagerDelete: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Only owner can access this page
  if (!hasPermission('manage_users')) {
    return (
      <div className="text-center py-12">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">Only the owner can access admin settings.</p>
      </div>
    );
  }

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Load settings from localStorage or Firebase
      const savedSettings = localStorage.getItem('adminSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Save to localStorage (you can also save to Firebase if needed)
      localStorage.setItem('adminSettings', JSON.stringify(settings));
      
      // Update environment variable for sales manager email
      if (settings.salesManagerEmail) {
        // Note: This would require app restart to take effect
        console.log('Sales Manager Email:', settings.salesManagerEmail);
      }
      
      toast.success('Settings saved successfully! Please restart the app for changes to take effect.');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const permissionItems = [
    {
      key: 'allowManagerPurchases',
      title: 'Allow Purchase Management',
      description: 'Sales manager can add, edit, and view purchases',
      icon: '🛒'
    },
    {
      key: 'allowManagerSuppliers',
      title: 'Allow Supplier Management',
      description: 'Sales manager can add, edit, and view suppliers',
      icon: '🏢'
    },
    {
      key: 'allowManagerReports',
      title: 'Allow Reports Access',
      description: 'Sales manager can view and export reports',
      icon: '📊'
    },
    {
      key: 'allowManagerInventory',
      title: 'Allow Inventory Access',
      description: 'Sales manager can view inventory dashboard',
      icon: '📦'
    },
    {
      key: 'allowManagerDelete',
      title: 'Allow Record Deletion',
      description: 'Sales manager can delete records (use with caution)',
      icon: '🗑️'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Admin Settings
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage user permissions and system settings
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <div className="spinner mr-2"></div>
            ) : (
              <CheckIcon className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </button>
        </div>
      </div>

      {/* Sales Manager Email */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <UserIcon className="h-6 w-6 text-blue-500 mr-3" />
          <h3 className="text-lg font-medium text-gray-900">Sales Manager Account</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sales Manager Email
            </label>
            <input
              type="email"
              value={settings.salesManagerEmail}
              onChange={(e) => handleSettingChange('salesManagerEmail', e.target.value)}
              className="input"
              placeholder="manager@a2ztak.in"
            />
            <p className="mt-1 text-sm text-gray-500">
              Enter the email address for your sales manager. They will need a Firebase account with this email.
            </p>
          </div>
        </div>
      </div>

      {/* Permission Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-6">
          <ShieldCheckIcon className="h-6 w-6 text-green-500 mr-3" />
          <h3 className="text-lg font-medium text-gray-900">Sales Manager Permissions</h3>
        </div>
        
        <div className="space-y-4">
          {permissionItems.map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{item.icon}</span>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                  <p className="text-sm text-gray-500">{item.description}</p>
                </div>
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => handleSettingChange(item.key, !settings[item.key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                    settings[item.key] ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings[item.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="ml-3 text-sm text-gray-500">
                  {settings[item.key] ? (
                    <span className="text-green-600 flex items-center">
                      <CheckIcon className="h-4 w-4 mr-1" />
                      Enabled
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XMarkIcon className="h-4 w-4 mr-1" />
                      Disabled
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Default Permissions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <CogIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Default Permissions</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Sales managers always have access to:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>View dashboard</li>
                <li>Add and edit sales entries</li>
                <li>Generate PDF invoices</li>
                <li>Export sales data</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Restart Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <CogIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Important</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>After changing settings, you need to:</p>
              <ol className="list-decimal list-inside mt-1 space-y-1">
                <li>Restart the application (stop and run <code>npm start</code> again)</li>
                <li>Create Firebase account for sales manager if email is new</li>
                <li>Sales manager should logout and login again</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
